import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowDown, ArrowUp, AtSign, BellOff, BellRing, Check, CheckCheck,
  CornerDownRight, LoaderCircle, MapPin, MessageCircle, Mic, MoreHorizontal,
  Paperclip, Pencil, RotateCcw, Square, Trash2, X,
} from 'lucide-react';
import { fmtDate } from '../../architect/utils';
import { useAuth } from '../../auth/AuthContext';
import {
  COMMENT_STAGE_LABEL, CommentAnchor, CommentPerson, CommentStage, CommentThread,
  StageComment, commentsApi, filterMentionable, mentionQuery, personLabel, splitMentions,
} from '../../architect/api';
import {
  CommentMedia, QueuedAttachment, QueuedCommentFile, commentFileLimit,
} from '../../architect/CommentAttachments';
import { useCommentVoice } from '../../architect/useCommentVoice';
import { useCommentUpdates } from '../../architect/useCommentUpdates';
import { useCommentSound } from '../../architect/useCommentSound';
import { Sheet } from './Sheet';
import { haptic, showConfirm } from '../telegram';
import './tma-comments.css';

interface Props {
  open: boolean;
  onClose: () => void;
  projectId: string;
  stage: CommentStage;
  onChanged?: () => void;
  anchor?: CommentAnchor | null;
  focusedThreadId?: string | null;
  onClearAnchor?: () => void;
  onPickAnchor?: () => void;
  onJumpToAnchor?: (anchor: CommentAnchor) => void;
  onBusyChange?: (busy: boolean) => void;
  onDraftStateChange?: (state: { dirty: boolean; busy: boolean }) => void;
}

interface Draft {
  text: string;
  mentions: CommentPerson[];
  files: QueuedCommentFile[];
  replyId: string | null;
  editId: string | null;
  editBody: string;
}
const emptyDraft = (): Draft => ({ text: '', mentions: [], files: [], replyId: null, editId: null, editBody: '' });
// A sheet may unmount when the user changes stages. Keep unsent messages and
// selected files for this browser session, separately for every project/stage.
const drafts = new Map<string, Draft>();
const ordered = (items: StageComment[]) => Array.from(new Map(items.map(item => [item.id, item])).values())
  .sort((a, b) => a.created_at.localeCompare(b.created_at) || a.id.localeCompare(b.id));
const merge = (old: StageComment[], next: StageComment[]) => ordered([...old, ...next]);

export function CommentsSheet(props: Props) {
  return <Conversation key={`${props.projectId}:${props.stage}`} {...props} />;
}

function Conversation({
  open, onClose, projectId, stage, onChanged, anchor, focusedThreadId,
  onClearAnchor, onPickAnchor, onJumpToAnchor, onBusyChange, onDraftStateChange,
}: Props) {
  const { user } = useAuth();
  const key = `${projectId}:${stage}`;
  const [thread, setThread] = useState<CommentThread | null>(null);
  const threadRef = useRef<CommentThread | null>(null);
  const [draft, setDraft] = useState<Draft>(() => drafts.get(key) || emptyDraft());
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [sending, setSending] = useState(false);
  const [actionId, setActionId] = useState<string | null>(null);
  const [menuId, setMenuId] = useState<string | null>(null);
  const [showResolved, setShowResolved] = useState(false);
  const [pick, setPick] = useState<{ start: number; query: string } | null>(null);
  const [newCount, setNewCount] = useState(0);
  const [focusedCursor, setFocusedCursor] = useState<string | null>(null);
  const [loadingFocused, setLoadingFocused] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const busyLoad = useRef(false);
  const queuedLoad = useRef(false);
  const reloadRef = useRef<() => void>(() => {});
  const seen = useRef<Set<string> | null>(null);
  const requestId = useRef(0);
  const atBottom = useRef(true);
  const sound = useCommentSound();
  const soundRef = useRef(sound.play);
  soundRef.current = sound.play;
  const changedRef = useRef(onChanged);
  changedRef.current = onChanged;
  const busyChangeRef = useRef(onBusyChange);
  busyChangeRef.current = onBusyChange;
  const draftChangeRef = useRef(onDraftStateChange);
  draftChangeRef.current = onDraftStateChange;

  const patchDraft = useCallback((patch: Partial<Draft>) => {
    const next = { ...draftRef.current, ...patch };
    draftRef.current = next;
    drafts.set(key, next);
    setDraft(next);
  }, [key]);
  const commit = useCallback((next: CommentThread) => {
    threadRef.current = next;
    setThread(next);
  }, []);
  const scrollEnd = useCallback(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
    atBottom.current = true;
    setNewCount(0);
  }, []);
  const load = useCallback(async (quiet = false) => {
    if (busyLoad.current) { queuedLoad.current = true; return; }
    busyLoad.current = true;
    const id = ++requestId.current;
    if (!quiet) setLoading(true);
    try {
      const next = await commentsApi.thread(projectId, stage, undefined,
        quiet ? threadRef.current?.comments.slice(-100).map(comment => comment.id) : undefined);
      if (id !== requestId.current) return;
      const incoming = seen.current ? next.comments.filter(comment =>
        !seen.current!.has(comment.id) && !comment.mine && !comment.deleted && comment.author.id !== user?.id) : [];
      if (!seen.current) seen.current = new Set();
      next.comments.forEach(comment => seen.current!.add(comment.id));
      const old = threadRef.current;
      commit({ ...next, comments: merge(old?.comments || [], next.comments),
        // A live refresh reads the newest page. It must not revive an older
        // pagination cursor after the reader has already reached the start.
        next_cursor: quiet && old ? old.next_cursor : next.next_cursor });
      if (incoming.length) {
        soundRef.current();
        changedRef.current?.();
        if (!atBottom.current) setNewCount(count => count + incoming.length);
      }
      if (!old || atBottom.current) window.requestAnimationFrame(scrollEnd);
      setError('');
    } catch (err) {
      if (id === requestId.current && !quiet) setError((err as Error).message || 'Could not load comments');
    } finally {
      if (id === requestId.current) {
        busyLoad.current = false;
        setLoading(false);
        if (queuedLoad.current) { queuedLoad.current = false; reloadRef.current(); }
      }
    }
  }, [projectId, stage, user?.id, commit, scrollEnd]);
  reloadRef.current = () => void load(true);
  const connection = useCommentUpdates(projectId, stage, () => void load(true), open);
  useEffect(() => {
    if (!open) return;
    void load();
    return () => { requestId.current += 1; busyLoad.current = false; };
  }, [open, load]);
  useEffect(() => {
    if (!open || !focusedThreadId) return;
    let cancelled = false;
    void commentsApi.thread(projectId, stage, undefined, undefined, focusedThreadId).then(result => {
      if (cancelled) return;
      result.comments.forEach(item => seen.current?.add(item.id));
      const old = threadRef.current;
      commit({ ...result, ...old, comments: merge(old?.comments || [], result.comments) });
      setFocusedCursor(result.next_cursor || null);
      window.setTimeout(() => document.querySelector(`[data-tma-comment-id="${focusedThreadId}"]`)?.scrollIntoView({ block: 'center' }), 60);
    }).catch(err => { if (!cancelled) setError((err as Error).message || 'Could not open thread'); });
    return () => { cancelled = true; };
  }, [open, focusedThreadId, projectId, stage, commit]);
  useEffect(() => { if (open && anchor) inputRef.current?.focus(); }, [open, anchor]);

  const addFiles = useCallback((files: File[], voice = false, duration?: number) => {
    const current = draftRef.current;
    const accepted: QueuedCommentFile[] = [];
    let issue = '';
    for (const file of files) {
      if (current.files.length + accepted.length >= 10) { issue = 'Up to 10 files per message. Send these first to add more.'; break; }
      if (!file.size) { issue = `${file.name} is empty.`; continue; }
      if (file.size > commentFileLimit(file)) { issue = `${file.name} is too large. Images: 20 MB, videos: 50 MB, other files: 25 MB.`; continue; }
      accepted.push({ id: `${Date.now()}-${Math.random().toString(36).slice(2)}`, file, voice, duration });
    }
    patchDraft({ files: current.files.concat(accepted) });
    setError(issue);
  }, [patchDraft]);
  const voice = useCommentVoice((file, duration) => addFiles([file], true, duration), setError);
  const busy = sending || !!actionId || voice.recording || voice.requesting || voice.finalizing;
  useEffect(() => { busyChangeRef.current?.(busy); return () => busyChangeRef.current?.(false); }, [busy]);
  const dirty = Array.from(drafts.entries()).some(([draftKey, value]) =>
    draftKey.startsWith(`${projectId}:`) && (!!value.text.trim() || !!value.files.length || !!value.replyId || !!value.editId));
  useEffect(() => { draftChangeRef.current?.({ dirty, busy }); }, [dirty, busy]);
  const requestClose = () => {
    if (busy) { setError(voice.recording ? 'Finish or cancel the voice recording first.' : 'Wait for this action to finish.'); return; }
    onClose();
  };

  const candidates = useMemo(() => pick && thread ? filterMentionable(thread.mentionable, pick.query, user?.id) : [], [pick, thread, user?.id]);
  const insertMention = (person: CommentPerson) => {
    const caret = inputRef.current?.selectionStart ?? draftRef.current.text.length;
    const start = pick?.start ?? caret;
    const text = draftRef.current.text.slice(0, start) + '@' + person.username + ' ' + draftRef.current.text.slice(caret);
    patchDraft({ text, mentions: draftRef.current.mentions.some(item => item.id === person.id) ? draftRef.current.mentions : draftRef.current.mentions.concat(person) });
    setPick(null);
    window.setTimeout(() => { inputRef.current?.focus(); inputRef.current?.setSelectionRange(start + person.username.length + 2, start + person.username.length + 2); }, 0);
  };
  const openPicker = () => {
    const text = draftRef.current.text;
    const caret = inputRef.current?.selectionStart ?? text.length;
    const insert = caret && !/\s$/.test(text.slice(0, caret)) ? ' @' : '@';
    patchDraft({ text: text.slice(0, caret) + insert + text.slice(caret) });
    setPick({ start: caret + insert.length - 1, query: '' });
    window.setTimeout(() => { inputRef.current?.focus(); inputRef.current?.setSelectionRange(caret + insert.length, caret + insert.length); }, 0);
  };
  const send = async () => {
    const current = draftRef.current;
    if (busy || !thread?.can_write || (!current.text.trim() && !current.files.length)) return;
    haptic.impact('medium');
    setSending(true); setError('');
    const uploaded: string[] = [];
    try {
      for (const item of current.files) {
        const result = await commentsApi.upload(projectId, stage, item.file, item.voice, item.duration);
        uploaded.push(result.attachment.id);
      }
      const ids = current.mentions.filter(person => current.text.includes('@' + person.username)).map(person => person.id);
      const { comment } = await commentsApi.post(projectId, stage, current.text.trim(), ids, {
        ...(current.replyId ? { parent_id: current.replyId } : anchor ? { anchor } : {}),
        ...(uploaded.length ? { attachment_ids: uploaded } : {}),
      });
      patchDraft({ text: '', files: [], mentions: [], replyId: null });
      onClearAnchor?.();
      seen.current?.add(comment.id);
      if (threadRef.current) commit({ ...threadRef.current, comments: merge(threadRef.current.comments, [comment]) });
      setPick(null);
      haptic.notify('success');
      changedRef.current?.();
      window.requestAnimationFrame(scrollEnd);
    } catch (err) {
      await Promise.allSettled(uploaded.map(id => commentsApi.discardAttachment(id)));
      haptic.notify('error');
      setError((err as Error).message || 'Could not send. Your draft is still here.');
    } finally { setSending(false); }
  };
  const older = async () => {
    const cursor = threadRef.current?.next_cursor;
    if (!cursor || loadingOlder) return;
    setLoadingOlder(true);
    const height = listRef.current?.scrollHeight || 0;
    try {
      const result = await commentsApi.thread(projectId, stage, cursor);
      if (threadRef.current) commit({ ...threadRef.current, comments: merge(threadRef.current.comments, result.comments), next_cursor: result.next_cursor });
      window.requestAnimationFrame(() => { if (listRef.current) listRef.current.scrollTop += listRef.current.scrollHeight - height; });
    } catch (err) { setError((err as Error).message || 'Could not load earlier comments'); }
    finally { setLoadingOlder(false); }
  };
  const olderReplies = async () => {
    if (!focusedThreadId || !focusedCursor || loadingFocused) return;
    setLoadingFocused(true);
    try {
      const result = await commentsApi.thread(projectId, stage, focusedCursor, undefined, focusedThreadId);
      if (threadRef.current) commit({ ...threadRef.current, comments: merge(threadRef.current.comments, result.comments) });
      setFocusedCursor(result.next_cursor || null);
    } catch (err) { setError((err as Error).message || 'Could not load earlier replies'); }
    finally { setLoadingFocused(false); }
  };
  const changeComment = async (comment: StageComment, action: 'delete' | 'resolve' | 'edit') => {
    if (actionId) return;
    if (action === 'delete' && !await showConfirm('Delete this message? Replies will remain.')) return;
    setActionId(comment.id); setMenuId(null); setError('');
    try {
      if (action === 'delete') {
        await commentsApi.remove(comment.id);
        if (threadRef.current) commit({ ...threadRef.current, comments: threadRef.current.comments.map(item => item.id === comment.id ? { ...item, body: '', attachments: [], deleted: true, can_edit: false, can_delete: false } : item) });
      } else {
        const result = action === 'resolve'
          ? await commentsApi.resolve(comment.root_id || comment.id, !comment.resolved_at)
          : await commentsApi.edit(comment.id, draftRef.current.editBody.trim(), comment.mentions.filter(person => draftRef.current.editBody.includes('@' + person.username)).map(person => person.id));
        if (threadRef.current) commit({ ...threadRef.current, comments: merge(threadRef.current.comments, [result.comment]) });
        if (action === 'edit') patchDraft({ editId: null, editBody: '' });
      }
      haptic.notify('success'); changedRef.current?.();
    } catch (err) { haptic.notify('error'); setError((err as Error).message || 'Could not update comment'); }
    finally { setActionId(null); }
  };

  const all = thread?.comments || [];
  const roots = all.filter(comment => !comment.parent_id);
  const discussionsHere = anchor ? roots.filter(comment => {
    const place = comment.anchor;
    if (!place || comment.deleted || comment.resolved_at || place.kind !== anchor.kind || place.document_id !== anchor.document_id) return false;
    return anchor.kind === 'block'
      ? place.path === anchor.path
      : Math.abs((place.x || 0) - (anchor.x || 0)) < .015 && Math.abs((place.y || 0) - (anchor.y || 0)) < .015;
  }) : [];
  const resolvedCount = roots.filter(comment => comment.resolved_at).length;
  const visibleRoots = roots.filter(comment => showResolved || !comment.resolved_at);
  const reply = all.find(comment => comment.id === draft.replyId);
  const canWrite = !!thread?.can_write;

  const message = (comment: StageComment, isReply = false) => (
    <article key={comment.id} data-tma-comment-id={comment.id} className={'tma-cmt-msg' + (isReply ? ' is-reply' : '') + (comment.mine ? ' mine' : '')}>
      <div className="tma-cmt-head">
        <span className="tma-cmt-avatar" aria-hidden="true">{(comment.author.name || '?').slice(0, 1).toUpperCase()}</span>
        <div className="tma-cmt-meta"><strong>{comment.author.name || 'Former teammate'}</strong><time dateTime={comment.created_at}>{fmtDate(comment.created_at)}{comment.edited_at ? ' · edited' : ''}</time></div>
        {(comment.can_edit || comment.can_delete) && !comment.deleted ? <div className="tma-cmt-menu-wrap"><button type="button" className="tma-cmt-icon" aria-label={`Actions for ${comment.author.name || 'comment'}`} aria-expanded={menuId === comment.id} onClick={() => setMenuId(menuId === comment.id ? null : comment.id)}><MoreHorizontal size={19} /></button>{menuId === comment.id ? <div className="tma-cmt-menu">{comment.can_edit ? <button type="button" onClick={() => { patchDraft({ editId: comment.id, editBody: comment.body }); setMenuId(null); }}><Pencil size={15} />Edit</button> : null}{comment.can_delete ? <button type="button" onClick={() => void changeComment(comment, 'delete')}><Trash2 size={15} />Delete</button> : null}</div> : null}</div> : null}
      </div>
      {comment.deleted ? <div className="tma-cmt-deleted">This message was deleted.</div> : draft.editId === comment.id ? <div className="tma-cmt-edit"><textarea aria-label="Edit comment" value={draft.editBody} maxLength={8000} onChange={event => patchDraft({ editBody: event.target.value })} /><div><button type="button" onClick={() => patchDraft({ editId: null, editBody: '' })}>Cancel</button><button type="button" disabled={!!actionId || (!draft.editBody.trim() && !comment.attachments?.length)} onClick={() => void changeComment(comment, 'edit')}>Save</button></div></div> : <>
        {comment.body ? <div className="tma-cmt-body">{splitMentions(comment.body, comment.mentions || []).map((run, i) => run.person ? <span className="tma-mention" key={i}>{run.text}</span> : <React.Fragment key={i}>{run.text}</React.Fragment>)}</div> : null}
        {comment.attachments?.length ? <div className="tma-cmt-media-list">{comment.attachments.map(item => <CommentMedia key={item.id} attachment={item} />)}</div> : null}
      </>}
      {!isReply && canWrite && !comment.deleted ? <div className="tma-cmt-actions"><button type="button" disabled={!!comment.resolved_at} onClick={() => { patchDraft({ replyId: comment.id }); onClearAnchor?.(); inputRef.current?.focus(); }}><CornerDownRight size={15} />Reply</button>{comment.can_resolve ? <button type="button" disabled={!!actionId} onClick={() => void changeComment(comment, 'resolve')}>{comment.resolved_at ? <RotateCcw size={15} /> : <CheckCheck size={15} />}{comment.resolved_at ? 'Reopen' : 'Resolve'}</button> : null}</div> : null}
    </article>
  );

  const footer = thread && !canWrite ? <div className="tma-cmt-readonly">You can read this conversation. Posting is unavailable for your role.</div> : (
    <div className="tma-cmt-composer">
      {!reply && discussionsHere.length ? <div className="tma-cmt-existing"><span>Already discussing this spot</span>{discussionsHere.slice(0, 2).map(item => <button key={item.id} type="button" disabled={busy} onClick={() => { patchDraft({ replyId: item.id }); onClearAnchor?.(); inputRef.current?.focus(); }}><CornerDownRight size={15} /><span>{item.body || 'Attachment thread'}</span><strong>Reply</strong></button>)}{discussionsHere.length > 2 ? <small>{discussionsHere.length - 2} more in the conversation above</small> : null}</div> : null}
      {reply || anchor ? <div className="tma-cmt-context">{reply ? <CornerDownRight size={17} /> : <MapPin size={17} />}<span><strong>{reply ? `Reply to ${reply.author.name || 'thread'}` : anchor?.label}</strong><small>{reply ? reply.body || 'Attachment' : anchor?.quote || 'Pinned to this document'}</small></span><button type="button" className="tma-cmt-icon" aria-label="Remove comment context" disabled={sending} onClick={() => { patchDraft({ replyId: null }); onClearAnchor?.(); }}><X size={17} /></button></div> : null}
      {draft.files.length ? <div className="tma-cmt-queue">{draft.files.map(item => <QueuedAttachment key={item.id} item={item} disabled={sending} onRemove={() => patchDraft({ files: draft.files.filter(file => file.id !== item.id) })} />)}</div> : null}
      {pick && candidates.length ? <div className="tma-cmt-pick" role="listbox" aria-label="Mention a teammate">{candidates.map(person => <button type="button" role="option" aria-selected={false} key={person.id} className="tma-cmt-opt" onPointerDown={event => { event.preventDefault(); insertMention(person); }}><span className="tma-cmt-opt-name">{personLabel(person)}</span><span className="tma-cmt-opt-handle">@{person.username}</span></button>)}</div> : null}
      <textarea ref={inputRef} className="tma-cmt-input" rows={2} maxLength={8000} aria-label={reply ? 'Write a reply' : 'Write a comment'} placeholder={reply ? 'Write a reply…' : 'Message the team or @mention someone…'} value={draft.text} disabled={busy || !canWrite} onChange={event => { patchDraft({ text: event.target.value }); setPick(mentionQuery(event.target.value, event.target.selectionStart)); }} onPaste={event => { const files = Array.from(event.clipboardData.files); if (files.length) { event.preventDefault(); addFiles(files); } }} />
      {voice.recording || voice.requesting || voice.finalizing ? <div className="tma-cmt-record" role="status"><span className="tma-cmt-record-dot" /><span>{voice.finalizing ? 'Finishing…' : voice.requesting ? 'Opening microphone…' : `Recording ${Math.floor(voice.seconds / 60)}:${String(voice.seconds % 60).padStart(2, '0')}`}</span><button type="button" onClick={voice.cancel}>Cancel</button>{voice.recording ? <button type="button" onClick={voice.stop}><Square size={14} fill="currentColor" />Done</button> : null}</div> : null}
      <div className="tma-cmt-controls"><div><button type="button" className="tma-cmt-icon" aria-label="Attach files" disabled={busy || !canWrite || draft.files.length >= 10} onClick={() => fileRef.current?.click()}><Paperclip size={20} /></button><input ref={fileRef} type="file" multiple hidden aria-label="Choose comment attachments" onChange={event => { addFiles(Array.from(event.target.files || [])); event.target.value = ''; }} /><button type="button" className="tma-cmt-icon" aria-label="Mention someone" disabled={busy || !canWrite} onClick={openPicker}><AtSign size={20} /></button><button type="button" className="tma-cmt-icon" aria-label="Record voice message" disabled={busy || !canWrite || draft.files.length >= 10} onClick={() => void voice.start()}><Mic size={20} /></button>{onPickAnchor ? <button type="button" className="tma-cmt-icon" aria-label="Pin a comment to this document" disabled={busy || !canWrite} onClick={onPickAnchor}><MapPin size={20} /></button> : null}</div><button type="button" className="tma-cmt-send" aria-label={reply ? 'Send reply' : 'Send comment'} disabled={busy || !canWrite || (!draft.text.trim() && !draft.files.length)} onClick={() => void send()}>{sending ? <LoaderCircle size={20} className="tma-cmt-spin" /> : <ArrowUp size={20} />}</button></div>
      <span className="tma-cmt-hint">{draft.files.length ? `${draft.files.length} of 10 attachments · Images, video, audio and files` : 'Files, video and multiple voice clips'}</span>
    </div>
  );

  return <Sheet open={open} onClose={requestClose} title={`${COMMENT_STAGE_LABEL[stage]} · Comments`} className="tma-cmt-sheet" headerAction={<div className="tma-cmt-head-actions"><button type="button" className="tma-cmt-sound" aria-label={sound.enabled ? 'Mute comment sounds' : 'Enable comment sounds'} aria-pressed={sound.enabled} onClick={sound.toggle}>{sound.enabled ? <BellRing size={19} /> : <BellOff size={19} />}</button><button type="button" className="tma-cmt-sound" aria-label="Close comments" onClick={requestClose}><X size={19} /></button></div>} footer={footer}>
    <div className="tma-cmt-toolbar"><span className={`tma-cmt-live is-${connection}`}><i />{connection === 'live' ? 'Live' : connection === 'connecting' ? 'Connecting…' : 'Reconnecting…'}</span>{resolvedCount ? <button type="button" aria-pressed={showResolved} onClick={() => setShowResolved(value => !value)}><Check size={15} />{showResolved ? 'Hide resolved' : `Resolved (${resolvedCount})`}</button> : null}</div>
    {error ? <div className="tma-cmt-error" role="alert"><span>{error}</span><button type="button" aria-label="Dismiss error" onClick={() => setError('')}><X size={16} /></button>{!thread && !loading ? <button type="button" onClick={() => void load()}>Retry</button> : null}</div> : null}
    <div className="tma-cmt-list" ref={listRef} onScroll={event => { const el = event.currentTarget; atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 70; if (atBottom.current) setNewCount(0); }}>
      {thread?.next_cursor ? <button type="button" className="tma-cmt-older" disabled={loadingOlder} onClick={() => void older()}>{loadingOlder ? <LoaderCircle size={16} className="tma-cmt-spin" /> : <ArrowDown size={16} />}Earlier comments</button> : null}
      {loading && !thread ? <div className="tma-cmt-loading"><LoaderCircle size={22} className="tma-cmt-spin" />Loading conversation…</div> : null}
      {!loading && !visibleRoots.length ? <div className="tma-cmt-empty"><MessageCircle size={28} /><strong>{roots.length ? 'All caught up' : 'Start a conversation'}</strong><span>{roots.length ? 'All discussions on this stage are resolved.' : 'Leave a note on this stage or comment on a specific block.'}</span></div> : null}
      {visibleRoots.map(root => <section key={root.id} className={'tma-cmt-thread' + (root.resolved_at ? ' resolved' : '') + (root.id === focusedThreadId ? ' focused' : '')}>{root.anchor ? <button type="button" className="tma-cmt-anchor" disabled={!onJumpToAnchor} onClick={() => onJumpToAnchor?.(root.anchor!)}><MapPin size={15} /><span>{root.anchor.label}{root.anchor.quote ? <small>“{root.anchor.quote}”</small> : null}</span>{root.resolved_at ? <Check size={15} /> : null}</button> : root.resolved_at ? <span className="tma-cmt-resolved"><Check size={14} />Resolved</span> : null}{message(root)}{focusedThreadId === root.id && focusedCursor ? <button type="button" className="tma-cmt-older" disabled={loadingFocused} onClick={() => void olderReplies()}>Earlier replies</button> : null}{all.filter(item => item.parent_id && (item.root_id === root.id || item.parent_id === root.id)).map(item => message(item, true))}</section>)}
    </div>
    {newCount ? <button type="button" className="tma-cmt-new" onClick={scrollEnd}><ArrowDown size={16} />{newCount} new {newCount === 1 ? 'comment' : 'comments'}</button> : null}
  </Sheet>;
}
